import { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  collection,
  doc,
  getDoc,
  getDocs,
  setDoc,
  serverTimestamp,
  deleteDoc,
  runTransaction,
} from "firebase/firestore";
import { getAuth, onAuthStateChanged } from "firebase/auth";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { db, storage } from "../../firebaseConfig";
import toast, { Toaster } from "react-hot-toast";
import dayjs from "dayjs";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

const cx = (...xs) => xs.filter(Boolean).join(" ");
const toNum = (v) => Number(String(v || "").replace(/[^0-9.\-]/g, "")) || 0;

export default function ContractorMeasurementSheetPage() {
  const { tenderId } = useParams();
  const navigate = useNavigate();
  const auth = getAuth();

  const [user, setUser] = useState(null);
  const [vendor, setVendor] = useState(null);

  const [tender, setTender] = useState(null);
  const [boqItems, setBoqItems] = useState([]);

  // ✅ measurements[itemNo] = array of sheets
  const [measurements, setMeasurements] = useState({});

  const [selectedItemNo, setSelectedItemNo] = useState(null);
  const [loading, setLoading] = useState(true);

  // full measurement sheet templates (PMC Excel se)
  const [itemTemplates, setItemTemplates] = useState({});

  /* ---------- LOGIN GUARD ---------- */
  useEffect(() => {
    const unsub = onAuthStateChanged(auth, (u) => {
      if (!u) {
        toast.error("Please login as Contractor/Vendor.");
        navigate("/tender/login");
      } else {
        setUser(u);
      }
    });
    return () => unsub();
  }, [auth, navigate]);

  /* ---------- LOAD VENDOR PROFILE ---------- */
  useEffect(() => {
    if (!user) return;

    getDoc(doc(db, "vendors", user.uid))
      .then((snap) => {
        if (!snap.exists()) {
          toast.error("Vendor profile not found.");
          navigate("/tender/login");
        } else {
          setVendor({ id: snap.id, ...snap.data() });
        }
      })
      .catch(() => toast.error("Vendor profile error"));
  }, [user, navigate]);

  /* ---------- LOAD TENDER + BOQ + MEASUREMENTS + TEMPLATE ---------- */
  useEffect(() => {
    if (!user || !vendor || !tenderId) return;

    (async () => {
      try {
        setLoading(true);

        // Tender
        const tSnap = await getDoc(doc(db, "tenders", tenderId));
        if (!tSnap.exists()) {
          toast.error("Tender not found.");
          return;
        }
        const t = { id: tSnap.id, ...tSnap.data() };

        if (t.awardedVendorUid && t.awardedVendorUid !== user.uid) {
          toast.error("This project is not assigned to your company.");
          navigate("/tender/contractor");
          return;
        }
        setTender(t);

        // BOQ
        const boqSnap = await getDocs(collection(db, "tenders", tenderId, "boq_items"));
        let list = [];
        boqSnap.forEach((d) => list.push({ id: d.id, ...d.data() }));
        list.sort((a, b) => (a.itemNo || 0) - (b.itemNo || 0));
        setBoqItems(list);
        setSelectedItemNo((prev) => prev ?? (list[0]?.itemNo || null));

        // ✅ Contractor measurements (multi sheets)
        const measSnap = await getDocs(collection(db, "tenders", tenderId, "measurements_contractor"));
        const m = {};
        measSnap.forEach((d) => {
          const data = d.data();
          if (data.itemNo == null) return;
          if (!m[data.itemNo]) m[data.itemNo] = [];
          m[data.itemNo].push({ id: d.id, ...data });
        });

        // latest first
        Object.keys(m).forEach((k) => {
          m[k].sort((a, b) => {
            const ta = a.updatedAt?.toMillis?.() || a.createdAt?.toMillis?.() || 0;
            const tb = b.updatedAt?.toMillis?.() || b.createdAt?.toMillis?.() || 0;
            return tb - ta;
          });
        });

        setMeasurements(m);

        // FULL SHEET TEMPLATE LOAD (PMC Excel)
        const msSnap = await getDoc(
          doc(db, "tenders", tenderId, "measurement_sheets", "FULL_MEASUREMENT_SHEET")
        );

        if (msSnap.exists()) {
          const data = msSnap.data();
          const rows = Array.isArray(data.rows) ? data.rows : [];
          const templates = {};
          let currentItemNo = null;

          rows.forEach((r) => {
            const itemNoCell =
              r["ITEM NO"] ??
              r["Item No."] ??
              r["Item No"] ??
              r["Item"] ??
              r["ITEM"];

            if (itemNoCell !== undefined && itemNoCell !== "") {
              const n = Number(itemNoCell);
              if (!isNaN(n) && n > 0) currentItemNo = n;
            }

            if (!currentItemNo) return;

            if (!templates[currentItemNo]) templates[currentItemNo] = [];
            templates[currentItemNo].push(r);
          });

          setItemTemplates(templates);
        } else {
          setItemTemplates({});
        }
      } catch (err) {
        console.error(err);
        toast.error("Error loading measurement sheet");
      } finally {
        setLoading(false);
      }
    })();
  }, [user, vendor, tenderId, navigate]);

  const selectedBoq = useMemo(() => {
    return boqItems.find((b) => b.itemNo === selectedItemNo) || null;
  }, [boqItems, selectedItemNo]);

  const templateRowsForItem = useMemo(() => {
    return (selectedItemNo && itemTemplates[selectedItemNo]) || null;
  }, [selectedItemNo, itemTemplates]);

  const selectedSheets = useMemo(() => {
    if (!selectedItemNo) return [];
    return measurements[selectedItemNo] || [];
  }, [measurements, selectedItemNo]);

  if (!user || !vendor) return null;

  return (
    <div className="min-h-screen bg-emerald-50 pb-10">
      <Toaster position="top-center" />

      <div className="max-w-6xl mx-auto px-4 py-6">
        {/* HEADER */}
        <div className="p-4 rounded-2xl bg-white shadow-lg border border-emerald-200 flex justify-between items-start mb-6">
          <div>
            <div className="text-xs text-gray-500 uppercase">CONTRACTOR MEASUREMENT SHEET</div>
            <div className="text-lg md:text-xl font-bold text-emerald-800">
              {tender?.title || tender?.projectName || "Project"}
            </div>
            <div className="text-[11px] text-slate-600">
              {tender?.ownerCompany || ""} · {tender?.city || tender?.district || ""} ·{" "}
              {tender?.state || "Gujarat"}
            </div>
          </div>

          <div className="text-right text-[11px]">
            <div>Vendor: {vendor?.profile?.companyName || "You"}</div>
            <div>Date: {dayjs().format("DD/MM/YYYY")}</div>
            <button
              onClick={() => navigate("/tender/contractor")}
              className="mt-2 text-emerald-700 underline"
            >
              ← Back to Dashboard
            </button>
          </div>
        </div>

        {loading ? (
          <div className="bg-white p-4 rounded-xl shadow border text-[12px]">Loading BOQ & Measurements…</div>
        ) : boqItems.length === 0 ? (
          <div className="bg-white p-4 rounded-xl shadow border text-[12px]">BOQ items not found.</div>
        ) : (
          <div className="grid grid-cols-3 gap-4">
            {/* LEFT: BOQ list */}
            <div className="bg-white p-3 rounded-xl shadow border overflow-auto max-h-[520px]">
              <div className="text-xs font-semibold text-emerald-700 mb-2">BOQ Items</div>
              {boqItems.map((b) => {
                const list = measurements[b.itemNo] || [];
                const prevTotal = list.reduce((s, x) => s + toNum(x.sheetQty || 0), 0);
                const boqQty = toNum(b.boqQty);
                const bal = boqQty - prevTotal;

                return (
                  <button
                    key={b.id}
                    onClick={() => setSelectedItemNo(b.itemNo)}
                    className={cx(
                      "w-full text-left p-2 rounded-lg border mb-2 text-[11px]",
                      selectedItemNo === b.itemNo
                        ? "bg-emerald-50 border-emerald-500"
                        : "bg-white border-slate-200 hover:bg-slate-50"
                    )}
                  >
                    <div className="font-semibold">#{b.itemNo} – {b.unit || ""}</div>
                    <div className="line-clamp-2 text-slate-600">{b.description}</div>
                    <div className="mt-1 text-[10px] text-slate-700">
                      BOQ: {boqQty} · Measured: {prevTotal ? prevTotal.toFixed(3) : "0"} · Bal:{" "}
                      {bal.toFixed(3)}
                    </div>
                  </button>
                );
              })}
            </div>

            {/* RIGHT */}
            <div className="col-span-2 bg-white p-3 rounded-xl shadow border">
              {!selectedBoq ? (
                <div className="text-[12px] text-slate-600">Select a BOQ item from left list.</div>
              ) : (
                <ContractorMeasurementForm
                  tender={tender}
                  tenderId={tenderId}
                  user={user}
                  vendor={vendor}
                  boqItem={selectedBoq}
                  templateRows={templateRowsForItem}
                  existingList={selectedSheets}
                  onSaved={(savedSheet) => {
                    setMeasurements((prev) => {
                      const itemNo = savedSheet.itemNo;
                      const arr = Array.isArray(prev[itemNo]) ? [...prev[itemNo]] : [];
                      const idx = arr.findIndex((x) => x.id === savedSheet.id);
                      if (idx >= 0) arr[idx] = savedSheet;
                      else arr.unshift(savedSheet);

                      arr.sort((a, b) => {
                        const ta = a.updatedAt?.toMillis?.() || a.createdAt?.toMillis?.() || 0;
                        const tb = b.updatedAt?.toMillis?.() || b.createdAt?.toMillis?.() || 0;
                        return tb - ta;
                      });
                      return { ...prev, [itemNo]: arr };
                    });
                  }}
                  onDeleted={(itemNo, deletedId) => {
                    setMeasurements((prev) => {
                      const arr = Array.isArray(prev[itemNo]) ? [...prev[itemNo]] : [];
                      return { ...prev, [itemNo]: arr.filter((x) => x.id !== deletedId) };
                    });
                  }}
                />
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

/* ============================================================
   MULTI SHEET FORM (CONTRACTOR)
   ============================================================ */

function ContractorMeasurementForm({
  tender,
  tenderId,
  user,
  vendor,
  boqItem,
  templateRows,
  existingList = [],
  onSaved,
  onDeleted,
}) {
  const [rows, setRows] = useState([]);
  const [saving, setSaving] = useState(false);

  const [activeSheetId, setActiveSheetId] = useState(null);
  const [isEditing, setIsEditing] = useState(true);
  const [localInvoiceNo, setLocalInvoiceNo] = useState("");

  const activeSheet = useMemo(() => {
    if (!activeSheetId) return null;
    return existingList.find((s) => s.id === activeSheetId) || null;
  }, [existingList, activeSheetId]);

  // template detail rows
  const templateDetailRows =
    Array.isArray(templateRows) && templateRows.length > 1 ? templateRows.slice(1) : [];

  const createBlankRow = (idx, defaultLabel = "") => ({
    id: `row-${boqItem.itemNo}-${idx}-${Date.now()}`,
    label: defaultLabel,
    nos: "",
    length: "",
    width: "",
    depth: "",
    qty: "",
    photos: [],
  });

  // reset on item change
  useEffect(() => {
    setActiveSheetId(null);
    setLocalInvoiceNo("");
    setIsEditing(true);

    if (templateDetailRows.length > 0) {
      const blankRows = templateDetailRows.map((row, idx) => {
        const label = (row["ITEM DESCRIPTION"] || row["Item Description"] || row["Description"] || "").toString();
        return createBlankRow(idx, label);
      });
      setRows(blankRows);
    } else {
      setRows([createBlankRow(0)]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [boqItem.itemNo, templateRows]);

  const boqQty = toNum(boqItem.boqQty);

  const prevTotal = useMemo(() => {
    return existingList.reduce((s, x) => s + toNum(x.sheetQty || 0), 0);
  }, [existingList]);

  const thisSheetQty = rows.reduce((sum, r) => sum + toNum(r.qty), 0);
  const aggregatedTotal = prevTotal + thisSheetQty;

  const remainingBefore = boqQty - prevTotal;
  const remainingAfter = boqQty - aggregatedTotal;

  const alreadyFull = remainingBefore <= 0.0001;
  const overLimit = aggregatedTotal - boqQty > 0.0001;

  // PMC lock only for active sheet
  const pmcLocked =
    activeSheet && (activeSheet.status === "pmc_approved" || activeSheet.lockedByPmc === true);

  const locked = pmcLocked || alreadyFull;

  const handleRowChange = (idx, field, value) => {
    if (locked) return;
    setRows((prev) =>
      prev.map((r, i) => {
        if (i !== idx) return r;
        const next = { ...r, [field]: value };
        const n = toNum(next.nos);
        const L = toNum(next.length);
        const W = toNum(next.width);
        const D = toNum(next.depth);
        if (n && L && W && D) next.qty = (n * L * W * D).toFixed(3);
        return next;
      })
    );
  };

  const handleAddRow = () => {
    if (locked) return;
    setRows((prev) => [...prev, createBlankRow(prev.length)]);
  };

  const handlePhotoChange = async (idx, event) => {
    if (locked) return;
    const file = event.target.files?.[0];
    if (!file) return;
    try {
      const row = rows[idx];
      const path = `tender_measurements_contractor/${tenderId}/${boqItem.itemNo}/${row.id}/${Date.now()}_${file.name}`;
      const sRef = ref(storage, path);
      await uploadBytes(sRef, file);
      const url = await getDownloadURL(sRef);
      setRows((prev) =>
        prev.map((r, i) =>
          i === idx
            ? {
                ...r,
                photos: [...(Array.isArray(r.photos) ? r.photos : []), { url, path, name: file.name }],
              }
            : r
        )
      );
      toast.success("Photo uploaded");
    } catch (e) {
      console.error(e);
      toast.error("Photo upload failed");
    }
  };

  const startNewSheet = () => {
    if (alreadyFull) {
      toast.error("BOQ quantity is fully measured. New sheet not allowed.");
      return;
    }
    setActiveSheetId(null);
    setLocalInvoiceNo("");
    setIsEditing(true);

    if (templateDetailRows.length > 0) {
      const blankRows = templateDetailRows.map((row, idx) => {
        const label = (row["ITEM DESCRIPTION"] || row["Item Description"] || row["Description"] || "").toString();
        return createBlankRow(idx, label);
      });
      setRows(blankRows);
    } else {
      setRows([createBlankRow(0)]);
    }
  };

  const loadSheetForEdit = (sheet) => {
    if (!sheet) return;
    if (sheet.status === "pmc_approved" || sheet.lockedByPmc) {
      toast.error("PMC approved sheet edit nahi ho sakti.");
      return;
    }
    setActiveSheetId(sheet.id);
    setLocalInvoiceNo(sheet.sheetInvoiceNo || "");
    setIsEditing(true);

    if (Array.isArray(sheet.rows) && sheet.rows.length) {
      setRows(
        sheet.rows.map((r, idx) => ({
          id: r.id || `row-${boqItem.itemNo}-${idx}-${Date.now()}`,
          label: r.label || "",
          nos: r.nos || "",
          length: r.length || "",
          width: r.width || "",
          depth: r.depth || "",
          qty: r.qty || "",
          photos: Array.isArray(r.photos) ? r.photos : [],
        }))
      );
    }
  };

  /* ===== SAVE (multi invoice) ===== */
  const handleSave = async () => {
    if (locked) {
      toast.error(pmcLocked ? "PMC Approved – locked." : "BOQ fully measured – locked.");
      return;
    }
    if (!rows.length) {
      toast.error("Add at least one measurement row.");
      return;
    }
    if (thisSheetQty <= 0) {
      toast.error("Enter measurement for at least one row.");
      return;
    }
    if (overLimit) {
      toast.error("Total measurement exceeds BOQ quantity.");
      return;
    }

    try {
      setSaving(true);

      // ✅ invoice only for NEW sheet
      let invoiceNo = localInvoiceNo;

      if (!invoiceNo) {
        const counterRef = doc(db, "tenders", tenderId, "contractor_sheet_counters", String(boqItem.itemNo));

        const sheetSeq = await runTransaction(db, async (tx) => {
          const snap = await tx.get(counterRef);
          const current = snap.exists() ? Number(snap.data().seq || 0) : 0;
          const next = current + 1;
          tx.set(counterRef, { seq: next, updatedAt: serverTimestamp() }, { merge: true });
          return next;
        });

        const invPrefix = `ITEM${boqItem.itemNo}`;
        invoiceNo = `${invPrefix}/MSHEET/${String(sheetSeq).padStart(4, "0")}`;
        setLocalInvoiceNo(invoiceNo);
      }

      const docId = `${boqItem.itemNo}__${invoiceNo.replaceAll("/", "_")}`;
      const sheetDate = dayjs().format("DD/MM/YYYY");

      const payload = {
        tenderId,
        itemNo: boqItem.itemNo,
        boqId: boqItem.id,
        unit: boqItem.unit || "",
        boqQty,

        sheetQty: thisSheetQty,
        contractorTotalQty: aggregatedTotal,

        rows,

        createdByUid: user.uid,
        createdByName: vendor?.profile?.companyName || user.email || "Contractor",
        createdAt: activeSheet?.createdAt || serverTimestamp(),
        updatedAt: serverTimestamp(),

        status: activeSheet?.status || "draft",
        lockedByPmc: activeSheet?.lockedByPmc || false,

        sheetInvoiceNo: invoiceNo,
        sheetDate,
      };

      await setDoc(doc(db, "tenders", tenderId, "measurements_contractor", docId), payload, { merge: false });

      // BOQ aggregate store
      await setDoc(
        doc(db, "tenders", tenderId, "boq_items", boqItem.id),
        {
          contractorMeasuredQty: aggregatedTotal,
          contractorMeasuredAt: serverTimestamp(),
        },
        { merge: true }
      );

      toast.success("Measurement saved.");
      onSaved && onSaved({ id: docId, ...payload });

      setIsEditing(false);
      setActiveSheetId(docId);
    } catch (e) {
      console.error(e);
      toast.error("Failed to save measurement.");
    } finally {
      setSaving(false);
    }
  };

  /* ===== DELETE (per sheet) ===== */
  const handleDelete = async (sheet) => {
    if (!sheet) return;
    if (sheet.status === "pmc_approved" || sheet.lockedByPmc) {
      toast.error("PMC approval ke baad delete nahi ho sakta.");
      return;
    }
    const ok = window.confirm(`Delete sheet ${sheet.sheetInvoiceNo || ""}?`);
    if (!ok) return;

    try {
      setSaving(true);
      await deleteDoc(doc(db, "tenders", tenderId, "measurements_contractor", sheet.id));
      toast.success("Sheet deleted.");
      onDeleted && onDeleted(boqItem.itemNo, sheet.id);

      // ✅ ADD: Recalculate contractorTotalQty after deletion (Measured reset fix)
      const allSnap = await getDocs(
        collection(db, "tenders", tenderId, "measurements_contractor")
      );

      let newTotal = 0;
      const sameItemDocs = [];

      allSnap.forEach((d) => {
        const data = d.data() || {};
        const sameBoq = String(data.boqId || "") === String(boqItem.id || "");
        const sameItemNo = Number(data.itemNo) === Number(boqItem.itemNo);
        if (sameBoq && sameItemNo) {
          sameItemDocs.push({ ref: d.ref, data });
          newTotal += toNum(data.sheetQty || 0);
        }
      });

      // update remaining docs with new contractorTotalQty
      for (const x of sameItemDocs) {
        await setDoc(
          x.ref,
          {
            contractorTotalQty: newTotal,
            updatedAt: serverTimestamp(),
          },
          { merge: true }
        );
      }

      // update BOQ item aggregate (PMC/RA generator bhi yahi dekh sakta hai)
      await setDoc(
        doc(db, "tenders", tenderId, "boq_items", boqItem.id),
        {
          contractorMeasuredQty: newTotal,
          contractorMeasuredAt: serverTimestamp(),
        },
        { merge: true }
      );

      toast.success(`Recalculated: ${newTotal.toFixed(3)} ${boqItem.unit || ""}`);

      // recalc aggregate in UI (firestore aggregate update optional—skip for now)
      if (activeSheetId === sheet.id) startNewSheet();
    } catch (e) {
      console.error(e);
      toast.error("Delete error.");
    } finally {
      setSaving(false);
    }
  };

  // READONLY system table helper
  const readOnlyFromTemplate = (r) => ({
    label: r["ITEM DESCRIPTION"] || r["Item Description"] || r["Description"] || "",
    nos: r["NOS"] || r["Nos"] || r["Nos."] || r["NOS "] || "",
    length: r["LENGTH"] || r["Length"] || "",
    width: r["WIDTH"] || r["Width"] || "",
    depth: r["DEPTH"] || r["Depth"] || "",
    qty: r["QTY"] || r["Quantity"] || r["QUANTITY"] || "",
  });

  return (
    <div className="space-y-4 text-[11px]">
      {/* header info */}
      <div className="border border-slate-200 rounded-xl p-3 bg-slate-50">
        <div className="flex justify-between gap-4">
          <div>
            <div className="font-semibold text-slate-800">
              Item #{boqItem.itemNo} – {boqItem.unit || ""}
            </div>
            <div className="text-[10px] text-slate-700">{boqItem.description}</div>

            {pmcLocked && (
              <div className="mt-1 inline-flex items-center px-2 py-0.5 rounded-full bg-red-100 text-[9px] text-red-700 font-semibold">
                PMC Approved / Locked – further changes not allowed.
              </div>
            )}
            {alreadyFull && !pmcLocked && (
              <div className="mt-1 inline-flex items-center px-2 py-0.5 rounded-full bg-amber-100 text-[9px] text-amber-700 font-semibold">
                BOQ quantity is fully measured. No further entry allowed.
              </div>
            )}
          </div>

          <div className="text-right text-[10px] text-slate-700">
            <div>BOQ Qty: {boqQty.toFixed(3)}</div>
            <div>Measured (all sheets): {prevTotal.toFixed(3)}</div>
            <div>This sheet qty: {thisSheetQty.toFixed(3)}</div>
            <div className="font-semibold">Total after save: {aggregatedTotal.toFixed(3)}</div>
            <div className={cx(overLimit ? "text-red-600" : "text-emerald-700")}>
              Balance after save: {Math.max(remainingAfter, 0).toFixed(3)}
            </div>
          </div>
        </div>
      </div>

      {/* SYSTEM TABLE (READONLY) */}
      {templateDetailRows.length > 0 && (
        <div className="border border-slate-300 rounded-xl overflow-auto">
          <div className="px-2 py-1 bg-slate-100 text-[10px] font-semibold text-slate-700">
            System Measurement Layout (As per Excel – Read Only)
          </div>
          <table className="min-w-full text-[10px] border-collapse">
            <thead className="bg-slate-100">
              <tr>
                <th className="border border-slate-300 px-2 py-1 text-left">Label / Description</th>
                <th className="border border-slate-300 px-2 py-1">Nos</th>
                <th className="border border-slate-300 px-2 py-1">Length</th>
                <th className="border border-slate-300 px-2 py-1">Width</th>
                <th className="border border-slate-300 px-2 py-1">Depth</th>
                <th className="border border-slate-300 px-2 py-1">Qty</th>
              </tr>
            </thead>
            <tbody>
              {templateDetailRows.map((row, idx) => {
                const r = readOnlyFromTemplate(row);
                return (
                  <tr key={`sys-${idx}`} className="odd:bg-white even:bg-slate-50">
                    <td className="border border-slate-300 px-2 py-1">{r.label}</td>
                    <td className="border border-slate-300 px-2 py-1 text-right">{r.nos}</td>
                    <td className="border border-slate-300 px-2 py-1 text-right">{r.length}</td>
                    <td className="border border-slate-300 px-2 py-1 text-right">{r.width}</td>
                    <td className="border border-slate-300 px-2 py-1 text-right">{r.depth}</td>
                    <td className="border border-slate-300 px-2 py-1 text-right">{r.qty}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* ENTRY FORM */}
      {isEditing && (
        <>
          <div className="border border-slate-300 rounded-xl overflow-auto">
            <div className="px-2 py-1 bg-emerald-50 text-[10px] font-semibold text-emerald-800">
              Contractor Measurement Entry (Editable)
            </div>

            <table className="min-w-full text-[10px] border-collapse">
              <thead className="bg-slate-100">
                <tr>
                  <th className="border border-slate-300 px-2 py-1 text-left">Label / Description</th>
                  <th className="border border-slate-300 px-2 py-1">Nos</th>
                  <th className="border border-slate-300 px-2 py-1">Length</th>
                  <th className="border border-slate-300 px-2 py-1">Width</th>
                  <th className="border border-slate-300 px-2 py-1">Depth</th>
                  <th className="border border-slate-300 px-2 py-1">Qty</th>
                  <th className="border border-slate-300 px-2 py-1">Photo</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r, idx) => (
                  <tr key={r.id} className="odd:bg-white even:bg-slate-50">
                    <td className="border border-slate-300 px-2 py-1">
                      <input
                        value={r.label}
                        onChange={(e) => handleRowChange(idx, "label", e.target.value)}
                        disabled={locked}
                        className={cx("w-full border border-slate-300 rounded px-1 py-0.5", locked && "bg-slate-100 cursor-not-allowed")}
                      />
                    </td>
                    <td className="border border-slate-300 px-1 py-1 text-right">
                      <input
                        value={r.nos}
                        onChange={(e) => handleRowChange(idx, "nos", e.target.value)}
                        disabled={locked}
                        className={cx("w-full border border-slate-300 rounded px-1 py-0.5 text-right", locked && "bg-slate-100 cursor-not-allowed")}
                      />
                    </td>
                    <td className="border border-slate-300 px-1 py-1 text-right">
                      <input
                        value={r.length}
                        onChange={(e) => handleRowChange(idx, "length", e.target.value)}
                        disabled={locked}
                        className={cx("w-full border border-slate-300 rounded px-1 py-0.5 text-right", locked && "bg-slate-100 cursor-not-allowed")}
                      />
                    </td>
                    <td className="border border-slate-300 px-1 py-1 text-right">
                      <input
                        value={r.width}
                        onChange={(e) => handleRowChange(idx, "width", e.target.value)}
                        disabled={locked}
                        className={cx("w-full border border-slate-300 rounded px-1 py-0.5 text-right", locked && "bg-slate-100 cursor-not-allowed")}
                      />
                    </td>
                    <td className="border border-slate-300 px-1 py-1 text-right">
                      <input
                        value={r.depth}
                        onChange={(e) => handleRowChange(idx, "depth", e.target.value)}
                        disabled={locked}
                        className={cx("w-full border border-slate-300 rounded px-1 py-0.5 text-right", locked && "bg-slate-100 cursor-not-allowed")}
                      />
                    </td>
                    <td className="border border-slate-300 px-1 py-1 text-right">
                      <input
                        value={r.qty}
                        onChange={(e) => handleRowChange(idx, "qty", e.target.value)}
                        disabled={locked}
                        className={cx("w-full border border-slate-300 rounded px-1 py-0.5 text-right", locked && "bg-slate-100 cursor-not-allowed")}
                      />
                    </td>
                    <td className="border border-slate-300 px-2 py-1 text-center">
                      <input
                        type="file"
                        accept="image/*"
                        onChange={(e) => handlePhotoChange(idx, e)}
                        disabled={locked}
                        className={cx("text-[9px]", locked && "cursor-not-allowed")}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="bg-slate-100 font-semibold">
                  <td className="border border-slate-300 px-2 py-1 text-right" colSpan={5}>
                    This sheet total
                  </td>
                  <td className="border border-slate-300 px-2 py-1 text-right">{thisSheetQty.toFixed(3)}</td>
                  <td className="border border-slate-300 px-2 py-1" />
                </tr>
              </tfoot>
            </table>
          </div>

          <div className="flex justify-between items-center mt-2">
            <button
              type="button"
              onClick={handleAddRow}
              disabled={locked}
              className={cx("px-3 py-1.5 rounded-lg border text-[11px]", locked ? "bg-slate-100 text-slate-400 cursor-not-allowed" : "hover:bg-slate-50")}
            >
              + Add Row
            </button>
            <button
              type="button"
              onClick={handleSave}
              disabled={saving || overLimit || locked}
              className={cx(
                "px-4 py-1.5 rounded-lg text-[11px] font-semibold text-white",
                saving || overLimit || locked ? "bg-emerald-300 cursor-not-allowed" : "bg-emerald-600 hover:bg-emerald-700"
              )}
            >
              {saving ? "Saving…" : "Save Measurement"}
            </button>
          </div>
        </>
      )}

      {/* SAVED SHEETS TABLE */}
      <div className="mt-4 border border-slate-300 rounded-xl overflow-hidden">
        <div className="px-3 py-2 bg-slate-900 text-[10px] font-semibold text-white flex items-center justify-between">
          <span>Saved Measurement Sheets</span>
          <button type="button" onClick={startNewSheet} className="px-2 py-0.5 rounded bg-white text-slate-900">
            + New / Edit Sheet
          </button>
        </div>

        <table className="min-w-full text-[10px] border-collapse">
          <thead className="bg-slate-100">
            <tr>
              <th className="border border-slate-300 px-2 py-1">Sr</th>
              <th className="border border-slate-300 px-2 py-1">Date</th>
              <th className="border border-slate-300 px-2 py-1 text-left">Invoice No</th>
              <th className="border border-slate-300 px-2 py-1 text-right">Sheet Qty</th>
              <th className="border border-slate-300 px-2 py-1">PDF</th>
              <th className="border border-slate-300 px-2 py-1">Edit</th>
              <th className="border border-slate-300 px-2 py-1">Delete</th>
            </tr>
          </thead>
          <tbody>
            {existingList.length === 0 ? (
              <tr>
                <td colSpan={7} className="border border-slate-300 px-2 py-2 text-center text-slate-500">
                  No measurement sheet saved yet.
                </td>
              </tr>
            ) : (
              existingList.map((sheet, idx) => {
                const isLocked = sheet.status === "pmc_approved" || sheet.lockedByPmc;
                return (
                  <tr key={sheet.id} className="odd:bg-white even:bg-slate-50">
                    <td className="border border-slate-300 px-2 py-1 text-center">{idx + 1}</td>
                    <td className="border border-slate-300 px-2 py-1 text-center">{sheet.sheetDate || "—"}</td>
                    <td className="border border-slate-300 px-2 py-1">{sheet.sheetInvoiceNo || "—"}</td>
                    <td className="border border-slate-300 px-2 py-1 text-right">{toNum(sheet.sheetQty || 0).toFixed(3)}</td>
                    <td className="border border-slate-300 px-2 py-1 text-center">
                      <button
                        type="button"
                        onClick={() => generateMeasurementPdf(tender, boqItem, sheet)}
                        className="px-2 py-0.5 rounded bg-slate-900 text-white"
                      >
                        PDF
                      </button>
                    </td>
                    <td className="border border-slate-300 px-2 py-1 text-center">
                      <button
                        type="button"
                        onClick={() => loadSheetForEdit(sheet)}
                        className={cx(
                          "px-2 py-0.5 rounded",
                          isLocked ? "bg-slate-100 text-slate-400 cursor-not-allowed" : "bg-emerald-100 text-emerald-800"
                        )}
                      >
                        Edit
                      </button>
                    </td>
                    <td className="border border-slate-300 px-2 py-1 text-center">
                      <button
                        type="button"
                        onClick={() => handleDelete(sheet)}
                        disabled={saving || isLocked}
                        className={cx(
                          "px-2 py-0.5 rounded",
                          saving || isLocked ? "bg-slate-100 text-slate-400 cursor-not-allowed" : "bg-red-50 text-red-700 border border-red-200 hover:bg-red-100"
                        )}
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* ---------- PDF GENERATOR FOR MEASUREMENT SHEET ---------- */
function generateMeasurementPdf(tender, boqItem, sheet) {
  try {
    const docp = new jsPDF("p", "mm", "a4");
    const pageWidth = docp.internal.pageSize.getWidth();
    const marginLeft = 15;
    const marginRight = 15;
    const contentWidth = pageWidth - marginLeft - marginRight;

    const title = tender?.title || tender?.projectName || "Measurement Sheet";
    const pacsName = tender?.ownerCompany || tender?.pacsName || tender?.pacsShortName || "";

    const contractorName = sheet?.createdByName || tender?.vendorName || "Contractor";
    const invoiceNo = sheet.sheetInvoiceNo || "MSHEET/0001";
    const sheetDate =
      sheet.sheetDate ||
      (sheet.updatedAt?.toDate ? dayjs(sheet.updatedAt.toDate()).format("DD/MM/YYYY") : dayjs().format("DD/MM/YYYY"));

    docp.setFont("helvetica", "bold");
    docp.setFontSize(14);
    docp.text("Measurement Sheet", pageWidth / 2, 16, { align: "center" });

    docp.setFont("helvetica", "normal");
    docp.setFontSize(9);

    const titleLines = docp.splitTextToSize(title, contentWidth);
    docp.text(titleLines, marginLeft, 24);
    let y = 24 + titleLines.length * 4;

    if (pacsName) {
      docp.text(`PACS: ${pacsName}`, marginLeft, y);
      y += 4;
    }

    docp.text(`Contractor: ${contractorName}`, marginLeft, y);
    y += 4;

    docp.text(`Invoice No: ${invoiceNo}`, marginLeft, y);
    docp.text(`Date: ${sheetDate}`, pageWidth - marginRight, y, { align: "right" });
    y += 6;

    docp.text(`Item #${boqItem.itemNo} – ${boqItem.unit || ""}`, marginLeft, y);
    y += 4;

    const descLines = docp.splitTextToSize(boqItem.description || "", contentWidth);
    docp.text(descLines, marginLeft, y);
    y += descLines.length * 4 + 3;

    const validRows = (sheet.rows || []).filter((r) => {
      return (
        (r.label && String(r.label).trim() !== "") ||
        toNum(r.nos) ||
        toNum(r.length) ||
        toNum(r.width) ||
        toNum(r.depth) ||
        toNum(r.qty)
      );
    });

    const body = validRows.map((r, idx) => [
      idx + 1,
      r.label || "",
      r.nos || "",
      r.length || "",
      r.width || "",
      r.depth || "",
      r.qty || "",
    ]);

    autoTable(docp, {
      startY: y,
      margin: { left: marginLeft, right: marginRight },
      head: [["Sr", "Label / Description", "Nos", "Length", "Width", "Depth", "Qty"]],
      body,
      theme: "grid",
      styles: { fontSize: 8, cellPadding: 1.5, lineWidth: 0.2, lineColor: [0, 0, 0] },
      headStyles: { fillColor: [230, 230, 230], textColor: [0, 0, 0], fontStyle: "bold", halign: "center" },
      columnStyles: {
        0: { cellWidth: 8, halign: "center" },
        1: { cellWidth: 70 },
        2: { cellWidth: 12, halign: "right" },
        3: { cellWidth: 14, halign: "right" },
        4: { cellWidth: 14, halign: "right" },
        5: { cellWidth: 14, halign: "right" },
        6: { cellWidth: 18, halign: "right" },
      },
    });

    const tableEndY = docp.lastAutoTable.finalY || y + 40;

    docp.setFont("helvetica", "bold");
    docp.setFontSize(10);
    docp.text(
      `This Sheet Qty: ${toNum(sheet.sheetQty || 0).toFixed(3)} ${boqItem.unit || ""}`,
      pageWidth - marginRight,
      tableEndY + 8,
      { align: "right" }
    );

    docp.save(`${invoiceNo.replace(/\//g, "_")}.pdf`);
  } catch (e) {
    console.error(e);
    toast.error("Measurement PDF generate karte waqt error aya.");
  }
}
